import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { DataTable, type Column, type DataTableFilter } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { ProductoPeek } from '../../components/producto/ProductoPeek';
import { toast, confirmar } from '../../lib/toast';
import { CotizacionDesdeProductosModal } from '../../components/producto/CotizacionDesdeProductosModal';
import { CatalogoProductos, VistaRapidaProducto } from '../../components/producto/CatalogoProductos';
import { ORDENES_CATALOGO, ordenarCatalogo, type OrdenCatalogo } from '../../../shared/catalogo-productos';
import { ACCION_LABEL, ACCIONES_COMERCIALES } from '../../../shared/rotacion';
import { AccionComercialBadge } from '../../components/producto/AccionComercial';

/** Estados de un producto todavía en juego. DataTable trata '|' como multi-match. */
const ESTADOS_ACTIVOS = 'VIGENTE|EN_FABRICACION|BUSCAR_SUSTITUTO';

type ProductoRow = {
  id: string;
  sku: string;
  ean: string | null;
  part_number: string | null;
  nombre: string;
  coste: number | null;
  /** Último precio de venta al cliente (ERP de APPROX o pedido de cliente). */
  precio_cliente: number | null;
  precio_cliente_fecha: string | null;
  eta_proxima?: string | null;
  pvpr: number | null;
  margen_pct: number | null;
  fecha_creacion: string;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  accion_comercial: string;
  marca_id: string;
  marca_nombre: string | null;
  cliente_id: string;
  cliente_codigo?: string | null;
  cliente_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_id: string | null;
  detalle_nombre: string | null;
  gama_id: string | null;
  gama_codigo: string | null;
  gama_nombre: string | null;
  proveedor_nombre: string | null;
  stock_actual: number | null;
  stock_tiendas: number | null;
  stock_fecha: string | null;
  ventas_l4wk: number | null;
  pendiente_recibir: number | null;
  /** Ventas mensuales de los 3 meses de referencia (1 = el más reciente). */
  ventas_mes_1: number | null;
  ventas_mes_2: number | null;
  ventas_mes_3: number | null;
  ventas_media_3m: number | string | null;
  /** Cuántos de los 3 meses tenían venta ≠ 0 (el divisor real de la media). */
  ventas_meses_computados: number | null;
  /** El mes más reciente venía sin cerrar en el informe: su cifra subirá. */
  ventas_mes_1_provisional: boolean | null;
  /** stock_actual / media mensual de venta. NULL si no hay stock o no hay venta. */
  meses_stock: number | string | null;
  uds_pedidas: number | null;
  /** uds_pedidas propias o, si no hay, el pendiente de recibir del cliente. */
  uds_pedidas_efectivas: number | null;
  revision_pendiente: boolean;
};

type Props = {
  /** Si se pasa, filtra los productos por cliente (GAME, TICNOVA…). */
  clienteCodigo?: string;
  /** Título alternativo cuando se renderiza dentro de la sección de un cliente. */
  title?: string;
  subtitle?: string;
};

/** Extrae pares únicos {id, nombre} presentes en las filas y los ordena alfabéticamente. */
function uniqueOptions(
  rows: ProductoRow[] | undefined,
  idKey: keyof ProductoRow,
  nameKey: keyof ProductoRow,
): { value: string; label: string }[] {
  if (!rows) return [];
  const seen = new Map<string, string>();
  for (const r of rows) {
    const id = r[idKey];
    const nm = r[nameKey];
    if (typeof id === 'string' && id && typeof nm === 'string' && nm) {
      if (!seen.has(id)) seen.set(id, nm);
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * Margen comercial del cliente a partir de coste + PVPr. El coste va SIN IVA y
 * el PVPr CON IVA (21%); para que el margen sea correcto se compara contra el
 * PVPr SIN IVA: (PVPr_sin_iva − coste) / PVPr_sin_iva. Se calcula en vivo desde
 * los campos de la fila para reflejar el valor real aunque el margen_pct
 * persistido haya quedado obsoleto.
 */
const IVA_GENERAL_PCT = 21;
function margenLive(coste: number | null, pvpr: number | null): number | null {
  if (coste == null || pvpr == null) return null;
  const c = Number(coste);
  const pConIva = Number(pvpr);
  if (!Number.isFinite(c) || !Number.isFinite(pConIva) || pConIva <= 0) return null;
  const p = pConIva / (1 + IVA_GENERAL_PCT / 100);
  if (p <= 0) return null;
  return Number((((p - c) / p) * 100).toFixed(2));
}

/**
 * Semanas de stock de un producto.
 *  - Sin stock (0 o null) → 0 (el producto "cubre" cero semanas de demanda).
 *  - Stock > 0 y sin ventas L4WK → Number.POSITIVE_INFINITY (cobertura infinita).
 *  - Resto → stock / (ventas_l4wk / 4), redondeado.
 * Filtros numberCompare lo usan tal cual; `=∞` no es un caso realista de UX.
 */
/** 'YYYY-MM-DD' → 'jun 2026', para las cabeceras de las columnas de mes. */
function mesEtiqueta(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return iso;
  const d = new Date(Date.UTC(y, m - 1, 1));
  const nombre = new Intl.DateTimeFormat('es-ES', { month: 'short', timeZone: 'UTC' }).format(d);
  return `${nombre.replace('.', '')} ${y}`;
}

function semanasStockDe(r: ProductoRow): number {
  const stock = Number(r.stock_actual);
  if (!Number.isFinite(stock) || stock <= 0) return 0;
  const ventas = Number(r.ventas_l4wk ?? 0);
  if (!Number.isFinite(ventas) || ventas <= 0) return Number.POSITIVE_INFINITY;
  return Math.round(stock / (ventas / 4));
}

/**
 * Semanas de stock de un cliente con ventas mensuales (TICNOVA): la cobertura
 * en meses pasada a semanas (52/12). Mismos casos límite que semanasStockDe.
 */
function semanasDesdeMeses(r: ProductoRow): number {
  const stock = Number(r.stock_actual);
  if (!Number.isFinite(stock) || stock <= 0) return 0;
  const meses = r.meses_stock == null ? NaN : Number(r.meses_stock);
  if (!Number.isFinite(meses)) return Number.POSITIVE_INFINITY;
  return Math.round(meses * 52 / 12);
}

function uniqueStringOptions(rows: ProductoRow[] | undefined, key: keyof ProductoRow): { value: string; label: string }[] {
  if (!rows) return [];
  const seen = new Set<string>();
  for (const r of rows) {
    const v = r[key];
    if (typeof v === 'string' && v) seen.add(v);
  }
  return Array.from(seen)
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((v) => ({ value: v, label: v }));
}

export function ProductosList({ clienteCodigo, title, subtitle }: Props = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Con los eliminados: el filtro de estado los oculta salvo que se elija «Eliminado».
  const qs = clienteCodigo ? `?cliente_codigo=${encodeURIComponent(clienteCodigo)}&incluir_eliminados=1` : '?incluir_eliminados=1';
  const productos = useQuery({
    queryKey: ['productos', 'listado-full', clienteCodigo ?? '*', 'con-eliminados'],
    queryFn: () => api.get<{ data: ProductoRow[]; meses_ventas?: string[] }>(`/productos/listado/full${qs}`),
  });

  const estados = useQuery({
    queryKey: ['list', '/admin/producto-estados'],
    queryFn: () => api.get<{ data: { codigo: string; nombre: string }[] }>('/admin/producto-estados'),
  });

  // Catálogos para los dropdowns de la barra de acciones masivas.
  const gamasAll = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const categoriasAll = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/categorias'),
  });
  const subcategoriasAll = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });
  const detallesAll = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string }[] }>('/admin/detalles'),
  });
  const proveedoresAll = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'),
  });

  const rows = productos.data?.data;

  // ── Selección múltiple
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGama, setBulkGama] = useState('');
  const [bulkCategoria, setBulkCategoria] = useState('');
  const [bulkSubcategoria, setBulkSubcategoria] = useState('');
  const [bulkDetalle, setBulkDetalle] = useState('');
  const [bulkEstado, setBulkEstado] = useState('');
  const [bulkProveedor, setBulkProveedor] = useState('');

  // Subcategorías filtradas por la categoría elegida (igual que en el form).
  const subcatsBulk = useMemo(() => {
    const all = subcategoriasAll.data?.data ?? [];
    if (!bulkCategoria) return all;
    return all.filter((s) => s.categoria_id === bulkCategoria);
  }, [bulkCategoria, subcategoriasAll.data]);

  // Detalles filtrados por la subcategoría elegida.
  const detallesBulk = useMemo(() => {
    const all = detallesAll.data?.data ?? [];
    if (!bulkSubcategoria) return all;
    return all.filter((d) => d.subcategoria_id === bulkSubcategoria);
  }, [bulkSubcategoria, detallesAll.data]);

  const bulkMut = useMutation({
    mutationFn: (updates: Record<string, string | null>) =>
      api.patch<{ updated: number }>('/productos/bulk', {
        ids: Array.from(selectedIds),
        updates,
      }),
    onSuccess: (res) => {
      toast.success(`Actualizados ${res.updated} producto(s)`);
      setSelectedIds(new Set());
      setBulkGama(''); setBulkCategoria(''); setBulkSubcategoria(''); setBulkDetalle(''); setBulkEstado(''); setBulkProveedor('');
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
    },
    onError: (err) => toast.error('No se pudieron actualizar los productos', (err as Error).message),
  });

  const applyBulk = async () => {
    const updates: Record<string, string | null> = {};
    if (bulkGama) updates.gama_id = bulkGama === '__none__' ? null : bulkGama;
    if (bulkCategoria) updates.categoria_id = bulkCategoria === '__none__' ? null : bulkCategoria;
    if (bulkSubcategoria) updates.subcategoria_id = bulkSubcategoria === '__none__' ? null : bulkSubcategoria;
    if (bulkDetalle) updates.detalle_id = bulkDetalle === '__none__' ? null : bulkDetalle;
    if (bulkEstado) updates.estado = bulkEstado;
    if (bulkProveedor) updates.proveedor_id = bulkProveedor === '__none__' ? null : bulkProveedor;
    if (Object.keys(updates).length === 0) {
      toast.info('Elige al menos un campo para aplicar');
      return;
    }
    if (!(await confirmar({ title: `¿Aplicar cambios a ${selectedIds.size} producto(s)?`, confirmLabel: 'Aplicar', danger: true }))) return;
    bulkMut.mutate(updates);
  };

  // Última fecha de actualización de stock entre las filas actuales — se
  // muestra a nivel informativo al lado de los filtros booleanos para que
  // el CEO vea sobre qué snapshot está basado el stock listado.
  const stockUltimaFecha = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    let max: string | null = null;
    for (const r of rows) {
      if (typeof r.stock_fecha === 'string' && (max == null || r.stock_fecha > max)) {
        max = r.stock_fecha;
      }
    }
    if (!max) return null;
    // Formato dd-mm-yyyy pedido por el CEO. Recibimos 'YYYY-MM-DD' o ISO.
    const slug = max.slice(0, 10);
    const parts = slug.split('-');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return slug;
  }, [rows]);

  // Filtros dinámicos: sólo opciones presentes en el listado actual.
  const marcaOptions = useMemo(() => uniqueOptions(rows, 'marca_id', 'marca_nombre'), [rows]);
  const clienteOptions = useMemo(() => uniqueOptions(rows, 'cliente_id', 'cliente_nombre'), [rows]);
  const gamaOptions = useMemo(() => uniqueOptions(rows, 'gama_id', 'gama_nombre'), [rows]);
  const categoriaOptions = useMemo(() => uniqueOptions(rows, 'categoria_id', 'categoria_nombre'), [rows]);
  const subcategoriaOptions = useMemo(() => uniqueOptions(rows, 'subcategoria_id', 'subcategoria_nombre'), [rows]);
  const detalleOptions = useMemo(() => uniqueOptions(rows, 'detalle_id', 'detalle_nombre'), [rows]);
  const estadosPresentes = useMemo(() => uniqueStringOptions(rows, 'estado'), [rows]);

  // Cruza con catálogo oficial para mostrar el nombre humano del estado.
  // Además incluimos la opción combinada "Vigentes + En fabricación" como
  // primera opción — el default del filtro. DataTable soporta valores con
  // '|' como multi-match.
  const estadoOptions = useMemo(() => {
    const catalogoMap = new Map<string, string>(
      (estados.data?.data ?? []).map((e) => [e.codigo, e.nombre]),
    );
    const base = estadosPresentes.map((o) => ({ value: o.value, label: catalogoMap.get(o.value) ?? o.value }));
    return [
      // BUSCAR_SUSTITUTO entra en el default: la referencia sigue viva y en
      // venta mientras se le busca reemplazo. Sin esto, marcar ese estado haría
      // desaparecer el producto del listado, que no es lo que espera nadie.
      { value: ESTADOS_ACTIVOS, label: 'Vigentes + en fabricación + buscar sustituto' },
      ...base,
    ];
  }, [estadosPresentes, estados.data]);

  // Qué bloque de ventas se muestra se decide por los DATOS que trae el
  // listado, no por el código de cliente: GAME manda informes semanales y
  // TICNOVA extractos mensuales, y un cliente futuro con otro formato encajará
  // sin tocar esto. Un listado sin filtro de cliente puede mostrar los dos.
  const filas = useMemo(() => productos.data?.data ?? [], [productos.data]);
  const hayVentasSemanales = useMemo(
    () => filas.some((r) => r.stock_tiendas != null || Number(r.ventas_l4wk ?? 0) !== 0),
    [filas],
  );
  const hayVentasMensuales = useMemo(
    () => filas.some((r) => r.ventas_mes_1 != null || r.ventas_mes_2 != null || r.ventas_mes_3 != null),
    [filas],
  );
  const mesesVentas = useMemo(() => productos.data?.meses_ventas ?? [], [productos.data]);

  // Orden de columnas (decisión del CEO, 22/10/2026): identificación y estado,
  // clasificación, stock (actual / cobertura / pedidas), precios y, al final,
  // las ventas.
  const columns = useMemo<Column<ProductoRow>[]>(() => {
    const cols: Column<ProductoRow>[] = [
      {
        key: 'sku',
        label: 'SKU',
        render: (r) => (
          <span className="flex items-center gap-1 font-mono text-xs">
            {r.revision_pendiente && (
              <span
                className="text-amber-600"
                title="Ficha incompleta — revisar"
                aria-label="Revisión pendiente"
              >
                ⚠️
              </span>
            )}
            {r.sku}
          </span>
        ),
        sortValue: (r) => r.sku,
        width: '7rem',
      },
      {
        key: 'ean',
        label: 'EAN',
        render: (r) => (r.ean
          ? <span className="font-mono text-xs">{r.ean}</span>
          : <span className="text-slate-300">—</span>),
        sortValue: (r) => r.ean ?? '',
        width: '9rem',
        defaultHidden: true,
      },
      {
        key: 'nombre',
        label: 'Nombre',
        render: (r) => (
          <span className="block max-w-[18rem] truncate text-sm" title={r.nombre}>
            {r.nombre}
          </span>
        ),
        sortValue: (r) => r.nombre,
      },
      {
        key: 'estado',
        label: 'Estado',
        render: (r) => <Badge color={r.estado_color} dot>{r.estado_nombre ?? r.estado}</Badge>,
        sortValue: (r) => r.estado_nombre ?? r.estado,
        groupable: true,
      },
      {
        key: 'accion_comercial',
        label: 'Acción comercial',
        render: (r) => <AccionComercialBadge accion={r.accion_comercial} />,
        sortValue: (r) => r.accion_comercial,
        groupable: true,
      },
    ];
    if (!clienteCodigo) {
      cols.push({ key: 'cliente_nombre', label: 'Cliente', sortValue: (r) => r.cliente_nombre ?? '', groupable: true });
    }
    cols.push(
      { key: 'gama_nombre', label: 'Gama', render: (r) => r.gama_nombre ?? '—', sortValue: (r) => r.gama_nombre ?? '', groupable: true },
      { key: 'subcategoria_nombre', label: 'Subcategoría', render: (r) => r.subcategoria_nombre ?? '—', sortValue: (r) => r.subcategoria_nombre ?? '', groupable: true },
      { key: 'detalle_nombre', label: 'Detalle', render: (r) => r.detalle_nombre ?? <span className="text-slate-300">—</span>, sortValue: (r) => r.detalle_nombre ?? '', groupable: true, defaultHidden: true },
      {
        key: 'stock_actual',
        label: 'Stock actual',
        align: 'right',
        render: (r) => (r.stock_actual != null ? fmt.int(r.stock_actual) : '—'),
        sortValue: (r) => Number(r.stock_actual),
        total: 'sum', totalFormat: (n) => fmt.int(n),
      },
    );

    // Stock de tiendas y cobertura en SEMANAS: solo con informes semanales (GAME).
    if (hayVentasSemanales) {
      cols.push(
        {
          key: 'stock_tiendas',
          label: 'Stock tiendas',
          align: 'right',
          render: (r) => (r.stock_tiendas != null ? fmt.int(r.stock_tiendas) : '—'),
          sortValue: (r) => Number(r.stock_tiendas),
          total: 'sum', totalFormat: (n) => fmt.int(n),
        },
        {
          key: 'semanas_stock',
          label: 'Semanas stock',
          align: 'right',
          render: (r) => {
            const stock = Number(r.stock_actual);
            if (!Number.isFinite(stock) || stock <= 0) return '—';
            const semanas = semanasStockDe(r);
            if (!Number.isFinite(semanas)) return '∞';
            const cls = semanas <= 20 ? 'font-bold text-rose-600' : '';
            return <span className={cls}>{fmt.int(semanas)}</span>;
          },
          sortValue: (r) => {
            const stock = Number(r.stock_actual);
            if (!Number.isFinite(stock) || stock <= 0) return -1;
            const semanas = semanasStockDe(r);
            return Number.isFinite(semanas) ? semanas : Number.MAX_SAFE_INTEGER;
          },
        },
      );
    }
    // Cobertura en MESES: solo con extractos mensuales (TICNOVA).
    if (hayVentasMensuales) {
      cols.push(
        {
          key: 'meses_stock',
          label: 'Meses stock',
          align: 'right',
          // Cobertura = stock actual / media mensual de venta. En meses, no en
          // semanas: el dato de venta de estos clientes es mensual.
          render: (r) => {
            if (Number(r.stock_actual ?? 0) <= 0) return <span className="text-slate-300">—</span>;
            if (r.meses_stock == null) return <span title="Con stock pero sin ventas en los meses de referencia">∞</span>;
            const meses = Number(r.meses_stock);
            const cls = meses <= 2 ? 'font-bold text-rose-600' : meses >= 12 ? 'text-amber-700' : '';
            return (
              <span className={cls} title={`Stock actual ÷ media mensual de venta = ${meses.toFixed(1)} meses de cobertura`}>
                {meses < 10 ? meses.toFixed(1) : fmt.int(Math.round(meses))}
              </span>
            );
          },
          sortValue: (r) => {
            if (Number(r.stock_actual ?? 0) <= 0) return -1;
            return r.meses_stock == null ? Number.MAX_SAFE_INTEGER : Number(r.meses_stock);
          },
        },
      );
    }

    cols.push(
      {
        key: 'uds_pedidas',
        label: 'Uds. pedidas',
        align: 'right',
        total: 'sum', totalValue: (r) => Number(r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0), totalFormat: (n) => fmt.int(n),
        // Si no tenemos pedidos propios del producto se muestra el pendiente de
        // recibir que declara el cliente en su último extracto (única fuente en
        // TICNOVA). El asterisco distingue de dónde sale el número.
        render: (r) => {
          const propias = Number(r.uds_pedidas ?? 0);
          const efectivas = Number(r.uds_pedidas_efectivas ?? propias);
          if (!efectivas) return <span className="text-slate-300">—</span>;
          if (propias > 0) return fmt.int(propias);
          return (
            <span title="Pendiente de recibir declarado por el cliente en su último extracto (no hay pedido propio en el sistema)">
              {fmt.int(efectivas)}<span className="text-slate-400">*</span>
            </span>
          );
        },
        sortValue: (r) => Number(r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0),
      },
      { key: 'coste', label: 'Coste', align: 'right', render: (r) => fmt.eur(r.coste), sortValue: (r) => Number(r.coste) },
      { key: 'pvpr', label: 'PVPr', align: 'right', render: (r) => fmt.eur(r.pvpr), sortValue: (r) => (r.pvpr != null ? Number(r.pvpr) : null) },
      {
        key: 'margen_pct',
        label: 'Margen',
        align: 'right',
        render: (r) => {
          const m = margenLive(r.coste, r.pvpr) ?? r.margen_pct;
          return fmt.pct(m);
        },
        sortValue: (r) => {
          const m = margenLive(r.coste, r.pvpr) ?? r.margen_pct;
          return Number(m);
        },
      },
    );

    // Ventas, al final: semanales (GAME) y/o mensuales (TICNOVA). Las
    // cabeceras de los meses llevan el mes real, que se mueve con cada import.
    if (hayVentasSemanales) {
      cols.push(
        {
          key: 'ventas_l4wk',
          label: 'Ventas L4WK',
          align: 'right',
          render: (r) => fmt.int(r.ventas_l4wk ?? 0),
          sortValue: (r) => Number(r.ventas_l4wk ?? 0),
          total: 'sum', totalFormat: (n) => fmt.int(n),
        },
      );
    }
    if (hayVentasMensuales) {
      const mesCol = (
        key: 'ventas_mes_1' | 'ventas_mes_2' | 'ventas_mes_3',
        idx: number,
        fallback: string,
      ): Column<ProductoRow> => ({
        key,
        label: mesesVentas[idx] ? mesEtiqueta(mesesVentas[idx]) : fallback,
        align: 'right',
        render: (r) => {
          if (r[key] == null) return <span className="text-slate-300">—</span>;
          const prov = idx === 0 && !!r.ventas_mes_1_provisional;
          return (
            <span
              className={prov ? 'text-amber-700' : undefined}
              title={prov ? 'Mes sin cerrar en el informe: la cifra subirá con el próximo envío' : undefined}
            >
              {fmt.int(r[key])}{prov ? '·' : ''}
            </span>
          );
        },
        sortValue: (r) => Number(r[key] ?? 0),
        total: 'sum', totalValue: (r) => (r[key] == null ? null : Number(r[key])), totalFormat: (n) => fmt.int(n),
      });
      cols.push(
        {
          key: 'ventas_media_3m',
          label: 'Media 3M',
          align: 'right',
          render: (r) => {
            if (r.ventas_media_3m == null) return <span className="text-slate-300">—</span>;
            const prov = !!r.ventas_mes_1_provisional;
            const n = r.ventas_meses_computados ?? 0;
            const base = `Media de los ${n} mes(es) con venta de los 3 de referencia (los meses sin dato o a 0 no cuentan).`;
            return (
              <span
                className={prov ? 'font-semibold text-amber-700' : 'font-semibold'}
                title={prov
                  ? `${base} OJO: ${mesesVentas[0] ? mesEtiqueta(mesesVentas[0]) : 'el último mes'} venía sin cerrar en el informe, así que la media está por debajo de la real.`
                  : base}
              >
                {fmt.int(Math.round(Number(r.ventas_media_3m)))}{prov ? '·' : ''}
              </span>
            );
          },
          sortValue: (r) => Number(r.ventas_media_3m ?? 0),
        },
        mesCol('ventas_mes_1', 0, 'Últ. mes'),
        mesCol('ventas_mes_2', 1, 'Mes -2'),
        mesCol('ventas_mes_3', 2, 'Mes -3'),
      );
    }

    cols.push(
      {
        key: 'acciones',
        label: 'Acciones',
        sortable: false,
        align: 'right',
        render: (r) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${clienteCodigo ? `/${clienteCodigo.toLowerCase()}` : ''}/productos/${r.id}`);
            }}
            className="cursor-pointer rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Abrir
          </button>
        ),
      },
    );
    return cols;
  }, [clienteCodigo, navigate, hayVentasSemanales, hayVentasMensuales, mesesVentas]);

  // Base de ruta por cliente, derivada de clienteCodigo (multi-cliente):
  // GAME→/game, TICNOVA→/ticnova, y cualquier cliente futuro sin hardcodear.
  // Sin clienteCodigo (vista de todos) → raíz '/productos'.
  const clienteBase = clienteCodigo ? `/${clienteCodigo.toLowerCase()}` : '';
  const newProductoHref = `${clienteBase}/productos/nuevo`;
  const productoDetalleHref = (id: string) => `${clienteBase}/productos/${id}`;
  // Panel lateral (peek): clic simple abre el resumen sin salir del listado; doble clic o «Abrir» van a la ficha.
  const [peek, setPeek] = useState<ProductoRow | null>(null);
  // Modal «Crear nueva cotización» sobre la selección.
  const [cotizarIds, setCotizarIds] = useState<string[] | null>(null);
  const onCotizacionesCreadas = (ids: string[]) => {
    setCotizarIds(null);
    setSelectedIds(new Set());
    qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
    // Con una sola cotización se abre para trabajar en ella; con varias, el listado.
    const codigo = clienteCodigo ?? rows?.find((r) => r.id === cotizarIds?.[0])?.cliente_codigo ?? null;
    if (!codigo) return;
    const base = `/${codigo.toLowerCase()}/cotizaciones`;
    navigate(ids.length === 1 ? `${base}/${ids[0]}` : base);
  };

  // Vista listado (tabla) o vista web/catálogo (tarjetas). En la URL para que
  // recargar o volver atrás conserve la vista; la ordenación del catálogo, igual.
  const [searchParams, setSearchParams] = useSearchParams();
  const pkVista = `productos-${clienteCodigo ?? 'todos'}_vista`;
  const pkOrden = `productos-${clienteCodigo ?? 'todos'}_orden`;
  const vistaCatalogo = searchParams.get(pkVista) === 'catalogo';
  const ordenCatalogo = (searchParams.get(pkOrden) ?? '') as OrdenCatalogo;
  const setParam = (k: string, v: string) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (v) next.set(k, v); else next.delete(k);
    return next;
  }, { replace: true });
  const [vistaRapida, setVistaRapida] = useState<ProductoRow | null>(null);

  const numSeleccionados = selectedIds.size;
  const allEstadosOptions = (estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }));
  const noneOpt = { value: '__none__', label: '— quitar valor —' };

  // Filas tal cual se ven en la tabla (búsqueda + filtros + orden) para exportar.
  const [vistaRows, setVistaRows] = useState<ProductoRow[]>([]);
  const [exporting, setExporting] = useState(false);

  const exportarExcel = async () => {
    const fuente = vistaRows.length > 0 ? vistaRows : (rows ?? []);
    if (fuente.length === 0) {
      toast.info('No hay productos que exportar');
      return;
    }
    setExporting(true);
    try {
      const payloadRows = fuente.map((r) => {
        const semanas = semanasStockDe(r);
        return {
          sku: r.sku,
          ean: r.ean,
          nombre: r.nombre,
          cliente: r.cliente_nombre,
          marca: r.marca_nombre,
          gama: r.gama_nombre,
          categoria: r.categoria_nombre,
          subcategoria: r.subcategoria_nombre,
          detalle: r.detalle_nombre,
          coste: r.coste,
          pvpr: r.pvpr,
          margen: margenLive(r.coste, r.pvpr) ?? r.margen_pct,
          stock_actual: r.stock_actual,
          stock_tiendas: r.stock_tiendas,
          ventas_l4wk: r.ventas_l4wk ?? 0,
          // ∞ (stock sin ventas) no es exportable como número → se omite.
          semanas_stock: Number.isFinite(semanas) ? semanas : null,
          // Bloque mensual: el servidor solo saca estas columnas si vienen con
          // dato, así que el Excel acaba con las mismas que ves en pantalla.
          ventas_media_3m: r.ventas_media_3m != null ? Math.round(Number(r.ventas_media_3m)) : null,
          ventas_mes_1: r.ventas_mes_1,
          ventas_mes_2: r.ventas_mes_2,
          ventas_mes_3: r.ventas_mes_3,
          meses_stock: r.meses_stock != null ? Number(Number(r.meses_stock).toFixed(1)) : null,
          uds_pedidas: r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0,
          estado: r.estado_nombre ?? r.estado,
        };
      });
      const { data: sess } = await (await import('../../lib/supabase')).supabase.auth.getSession();
      const token = sess.session?.access_token;
      const resp = await fetch('/api/productos/export-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          rows: payloadRows,
          includeCliente: !clienteCodigo,
          // Etiquetas de los meses para que el Excel diga "jun 2026" y no "mes -2".
          meses_labels: mesesVentas.map((m) => mesEtiqueta(m)),
        }),
      });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `productos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  // Memoizado para que su identidad sea estable entre renders: evita que el
  // useMemo de filteredRows (en DataTable) se recalcule en cada render.
  const filtros = useMemo<DataTableFilter<ProductoRow>[]>(() => [
    { key: 'marca_id', label: 'Marca', options: marcaOptions },
    ...(clienteCodigo ? [] : [{ key: 'cliente_id', label: 'Cliente', options: clienteOptions }]),
    { key: 'gama_id', label: 'Gama', options: gamaOptions },
    { key: 'categoria_id', label: 'Categoría', options: categoriaOptions },
    { key: 'subcategoria_id', label: 'Subcategoría', options: subcategoriaOptions },
    { key: 'detalle_id', label: 'Detalle', options: detalleOptions },
    { key: 'estado', label: 'Estado', options: estadoOptions, defaultValue: ESTADOS_ACTIVOS, hiddenUnlessSelected: ['ELIMINADO'] },
    { key: 'accion_comercial', label: 'Acción comercial', options: ACCIONES_COMERCIALES.map((a) => ({ value: a, label: ACCION_LABEL[a] })) },
    { key: 'semanas_stock', label: 'Semanas stock', type: 'numberCompare', getValue: (r) => semanasStockDe(r) },
    { key: 'con_stock', label: 'Con stock', type: 'boolean', getValue: (r) => Number(r.stock_actual ?? 0) > 0 },
    { key: 'pedidos_pdte', label: 'Pedidos pdte.', type: 'boolean', getValue: (r) => Number(r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0) > 0 },
    { key: 'revision_pendiente', label: '⚠️ Revisar ficha', type: 'boolean', getValue: (r) => Boolean(r.revision_pendiente) },
    // Peligro de rotura: producto vigente, sin nada pedido y con menos meses de
    // cobertura que el umbral que teclea el usuario al marcar el filtro (5 por
    // defecto). Al marcarlo, la tabla se ordena por meses de stock ascendente
    // para que lo más urgente quede arriba.
    // La cobertura es en MESES cuando el cliente manda ventas mensuales
    // (TICNOVA) y en SEMANAS cuando manda semanales (GAME): el filtro, su
    // umbral por defecto (5 meses / 20 semanas) y el orden siguen a la
    // unidad. Antes solo miraba meses_stock y en GAME no devolvía nada.
    {
      key: 'riesgo_rotura',
      label: '🚨 Peligro de rotura de stock',
      type: 'booleanParam',
      paramDefault: hayVentasMensuales ? 5 : 20,
      paramSuffix: hayVentasMensuales ? 'meses' : 'semanas',
      sortWhenActive: { key: hayVentasMensuales ? 'meses_stock' : 'semanas_stock' },
      getValueParam: (r, umbral) => {
        if (r.estado !== 'VIGENTE') return false;
        if (Number(r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0) > 0) return false;
        if (hayVentasMensuales) return r.meses_stock != null && Number(r.meses_stock) < umbral;
        const semanas = semanasStockDe(r);
        return Number.isFinite(semanas) && semanas > 0 && semanas < umbral;
      },
    },
  ], [clienteCodigo, marcaOptions, clienteOptions, gamaOptions, categoriaOptions, subcategoriaOptions, detalleOptions, estadoOptions, hayVentasMensuales]);

  return (
    <div>
      <PageHeader
        title={title ?? 'Productos'}
        subtitle={subtitle ?? 'Catálogo de productos comercializados para los clientes de Approx.'}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={exportarExcel}
              disabled={exporting}
              title="Descarga un Excel con los productos tal como están filtrados y ordenados en la tabla."
            >
              {exporting ? 'Generando…' : '📊 Exportar Excel'}
            </Button>
            <Button variant="create" onClick={() => navigate(newProductoHref)}>+ Nuevo producto</Button>
          </div>
        }
      />

      {/* Barra de acciones masivas — sólo visible con selección */}
      {numSeleccionados > 0 && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-sky-900">
              <span className="font-semibold uppercase tracking-wide">Cambios masivos</span>
              <span className="ml-2 text-sky-700">
                · {numSeleccionados} producto{numSeleccionados === 1 ? '' : 's'} seleccionado{numSeleccionados === 1 ? '' : 's'}
              </span>
            </span>
            <span className="flex items-center gap-3">
              <Button
                variant="create"
                size="sm"
                onClick={() => setCotizarIds(Array.from(selectedIds))}
                title="Crea una cotización nueva (fecha de hoy) por producto seleccionado con el coste nuevo y las condiciones vigentes."
              >
                ＋ Crear nueva cotización
              </Button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
              >
                Limpiar selección
              </button>
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Gama</label>
              <Select
                options={[noneOpt, ...(gamasAll.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))]}
                value={bulkGama}
                onChange={(e) => setBulkGama(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Categoría</label>
              <Select
                options={[noneOpt, ...(categoriasAll.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))]}
                value={bulkCategoria}
                onChange={(e) => { setBulkCategoria(e.target.value); setBulkSubcategoria(''); setBulkDetalle(''); }}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Subcategoría</label>
              <Select
                options={[noneOpt, ...subcatsBulk.map((s) => ({ value: s.id, label: s.nombre }))]}
                value={bulkSubcategoria}
                onChange={(e) => { setBulkSubcategoria(e.target.value); setBulkDetalle(''); }}
                emptyLabel="(sin cambio)"
                disabled={!bulkCategoria && subcatsBulk.length > 50}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Detalle</label>
              <Select
                options={[noneOpt, ...detallesBulk.map((d) => ({ value: d.id, label: d.nombre }))]}
                value={bulkDetalle}
                onChange={(e) => setBulkDetalle(e.target.value)}
                emptyLabel="(sin cambio)"
                disabled={!bulkSubcategoria && detallesBulk.length > 50}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Estado</label>
              <Select
                options={allEstadosOptions}
                value={bulkEstado}
                onChange={(e) => setBulkEstado(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Proveedor</label>
              <Select
                options={[noneOpt, ...(proveedoresAll.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))]}
                value={bulkProveedor}
                onChange={(e) => setBulkProveedor(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={applyBulk}
                disabled={bulkMut.isPending}
                className="w-full"
              >
                {bulkMut.isPending ? 'Aplicando…' : 'Aplicar a seleccionados'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs font-medium" role="group" aria-label="Tipo de vista">
          <button
            type="button"
            onClick={() => setParam(pkVista, '')}
            aria-pressed={!vistaCatalogo}
            className={`cursor-pointer px-3 py-1.5 ${!vistaCatalogo ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            ☰ Vista listado
          </button>
          <button
            type="button"
            onClick={() => { setParam(pkVista, 'catalogo'); setPeek(null); }}
            aria-pressed={vistaCatalogo}
            className={`cursor-pointer border-l border-slate-300 px-3 py-1.5 ${vistaCatalogo ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            ▦ Vista web / catálogo
          </button>
        </div>
        {vistaCatalogo && (
          <label className="flex items-center gap-1 text-xs text-slate-500">
            <span className="whitespace-nowrap">Ordenar:</span>
            <Select
              options={ORDENES_CATALOGO.filter((o) => o.value).map((o) => ({ value: o.value, label: o.label }))}
              emptyLabel={ORDENES_CATALOGO[0].label}
              value={ordenCatalogo}
              onChange={(e) => setParam(pkOrden, e.target.value)}
            />
          </label>
        )}
      </div>

      <DataTable<ProductoRow>
        columns={columns}
        rows={rows}
        loading={productos.isLoading}
        rowKey={(r) => r.id}
        storageKey={`productos-${clienteCodigo ?? 'todos'}`}
        // Orden por defecto: PVPr de menor a mayor (CEO, 26/10/2026), en la
        // tabla y en el catálogo («Orden del listado»). Sin PVPr, al final.
        defaultSort={{ key: 'pvpr' }}
        onRowClick={(r) => setPeek((p) => (p?.id === r.id ? null : r))}
        onRowDoubleClick={(r) => navigate(productoDetalleHref(r.id))}
        searchPlaceholder="Buscar por SKU, EAN, nombre o detalle…"
        stickyHeader
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onFilteredRowsChange={setVistaRows}
        filters={filtros}
        aside={vistaCatalogo ? undefined : peek
          ? <ProductoPeek producto={peek} clienteCodigo={clienteCodigo ?? (peek as { cliente_codigo?: string | null }).cliente_codigo ?? 'GAME'} onClose={() => setPeek(null)} />
          : (
            <div className="hidden rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-xs text-slate-400 xl:block">
              Pulsa un producto para ver aquí su resumen.<br />Doble clic abre la ficha.
            </div>
          )}
        renderBody={vistaCatalogo
          ? (filtradas) => (
            <CatalogoProductos
              rows={ordenarCatalogo(filtradas, ordenCatalogo)}
              clienteCodigo={clienteCodigo}
              loading={productos.isLoading}
              onVistaRapida={setVistaRapida}
              margen={margenLive}
              semanasStock={(r) => (hayVentasMensuales ? semanasDesdeMeses(r) : semanasStockDe(r))}
            />
          )
          : undefined}
        filterExtras={
          <span
            className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
            title="Fecha del snapshot de stock más reciente entre los productos listados."
          >
            Stock actualizado: <span className="font-mono font-semibold text-slate-900">{stockUltimaFecha ?? '—'}</span>
          </span>
        }
      />
      <CotizacionDesdeProductosModal
        productoIds={cotizarIds}
        onClose={() => setCotizarIds(null)}
        onCreated={onCotizacionesCreadas}
      />
      <VistaRapidaProducto
        row={vistaRapida}
        clienteCodigo={clienteCodigo}
        ventasMensuales={hayVentasMensuales}
        margen={margenLive}
        semanasStock={semanasStockDe}
        onClose={() => setVistaRapida(null)}
        onAbrir={(r) => navigate(productoDetalleHref(r.id))}
      />
    </div>
  );
}
